import { RefObject, useCallback, useEffect, useRef, useState } from "react";

// Distance from the bottom (px) still considered "at the bottom", so sub-pixel
// rounding or a partially visible last line doesn't pause auto-scroll.
const BOTTOM_THRESHOLD = 40;

const VIEWPORT_SELECTOR = "[data-radix-scroll-area-viewport]";

/**
 * Keeps a scroll container (a Radix ScrollArea or a plain overflow element)
 * pinned to the bottom as `trigger` changes (e.g. a live transcript list), but
 * pauses while the user reads older content.
 *
 * - Scrolling up pauses auto-scroll.
 * - Reaching the bottom again (manually or via `scrollToBottom`) resumes it.
 *
 * Only an upward move pauses: our own scrolls always move down, and a smooth
 * `scrollToBottom` fires intermediate "not at bottom yet" events that must not
 * pause it. Content shrinking clamps scrollTop but lands at the bottom anyway.
 *
 * @param rootRef ref to the ScrollArea root (the viewport is looked up inside),
 * or to the scrolling element itself.
 * @param trigger value whose changes should keep the list pinned.
 * @param options.resumeAtBottom when false, reaching the bottom by scrolling no
 * longer resumes auto-scroll — only `scrollToBottom` does (e.g. while a search
 * keeps the user parked on a match that happens to sit at the bottom).
 */
const useAutoScroll = <T>(
  rootRef: RefObject<HTMLElement>,
  trigger: T,
  { resumeAtBottom = true }: { resumeAtBottom?: boolean } = {}
) => {
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const isAutoScrollRef = useRef(true);
  const resumeAtBottomRef = useRef(resumeAtBottom);
  resumeAtBottomRef.current = resumeAtBottom;

  const getViewport = useCallback(() => {
    const root = rootRef.current;
    return root?.querySelector<HTMLElement>(VIEWPORT_SELECTOR) ?? root ?? null;
  }, [rootRef]);

  const setAutoScroll = useCallback((value: boolean) => {
    isAutoScrollRef.current = value;
    setIsAutoScroll(value);
  }, []);

  useEffect(() => {
    const viewport = getViewport();
    if (!viewport) return;
    let lastScrollTop = viewport.scrollTop;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const atBottom = scrollHeight - scrollTop - clientHeight <= BOTTOM_THRESHOLD;
      if (atBottom) {
        if (resumeAtBottomRef.current) setAutoScroll(true);
      } else if (scrollTop < lastScrollTop) {
        setAutoScroll(false);
      }
      lastScrollTop = scrollTop;
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [getViewport, setAutoScroll]);

  useEffect(() => {
    if (!isAutoScrollRef.current) return;
    const viewport = getViewport();
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [trigger, getViewport]);

  const scrollToBottom = useCallback(() => {
    setAutoScroll(true);
    const viewport = getViewport();
    viewport?.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
  }, [getViewport, setAutoScroll]);

  /** Pauses auto-scroll and centers `element` inside the viewport. */
  const revealElement = useCallback(
    (element: HTMLElement) => {
      const viewport = getViewport();
      if (!viewport) return;
      setAutoScroll(false);
      const viewportRect = viewport.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const offset = elementRect.top - viewportRect.top - (viewportRect.height - elementRect.height) / 2;
      viewport.scrollTo({ top: viewport.scrollTop + offset, behavior: "smooth" });
    },
    [getViewport, setAutoScroll]
  );

  return { isAutoScroll, scrollToBottom, revealElement };
};

export default useAutoScroll;
